import { isRetryable } from '../api/errors';
import { clientPushDevicesApi } from '../api/pushDevices';
import { getCustomerSession, isApiCustomer } from './customerSession';
import { getCapacitorPushPlugin, isNativeShell, nativePushToken } from './driverPush';
import { retryRegistration } from './pushRetry';

/**
 * A client's delivery updates on their phone (Rio, 2 Oct 2026; InGo
 * `Modules/Push`, `DeliveryUpdate`): the phone registered on the client's
 * token, the channel the updates arrive on, and a tap that opens the
 * delivery. Unlike a rider's offer, nothing here needs app code to draw the
 * notification: Android shows it from the message itself.
 */

/**
 * The channel the server names (`DeliveryUpdate::CHANNEL_ID`). Nothing can
 * check that the two agree: a message naming a channel the app never created
 * is shown on the default one, at ordinary importance (background-push
 * rule 5). Rename both in one change.
 */
export const DELIVERY_UPDATES_CHANNEL_ID = 'ingo_delivery_updates';

const PARKED_KEY = 'ingo_pending_client_tap';
const TAP_EVENT = 'ingo-client-update-tap';

let channelReady = false;

/** Creates the updates channel. Safe to call again; Android ignores a re-create. */
async function ensureUpdatesChannel(Push) {
  if (channelReady || typeof Push?.createChannel !== 'function') return;
  try {
    await Push.createChannel({
      id: DELIVERY_UPDATES_CHANNEL_ID,
      name: 'Delivery updates',
      description: 'How your deliveries are going.',
      importance: 4, // IMPORTANCE_DEFAULT is 3; 4 is a heads-up for "a rider is coming".
      visibility: 1, // Public on the lock screen: the words never name anyone but the rider's first name.
      vibration: true,
    });
    channelReady = true;
  } catch (e) {
    console.warn(`[clientPush] channel not created: ${e?.message || e}`);
  }
}

/**
 * Registers this phone for the signed-in client's delivery updates. Called on
 * every client sign-in and app start; says what it did either way, so "never
 * asked" and "asked and failed" are different lines.
 *
 * @returns {Promise<boolean>} true only when the server has the phone.
 */
export async function ensureClientPushRegistered() {
  if (!isApiCustomer()) {
    console.info('[clientPush] no InGo client signed in: not registering');
    return false;
  }
  if (!isNativeShell()) {
    console.info('[clientPush] not the phone app: updates show in the app');
    return false;
  }
  const id = String(getCustomerSession()?.id || '');
  const res = await registerClientPhone();
  if (res.ok) return true;
  // A token Google handed out late, or an API not reached: tried again while
  // this client stays signed in (pushRetry.js).
  if (res.retryable) retryRegistration(`customer:${id}`, () => retryClientRegistration(id));
  return false;
}

/** @returns {Promise<{ ok: boolean, retryable?: boolean }>} */
async function registerClientPhone() {
  const Push = await getCapacitorPushPlugin();
  await ensureUpdatesChannel(Push);
  const native = await nativePushToken();
  if (!native.ok) {
    console.warn(`[clientPush] no push token ${JSON.stringify({ error: native.error || '', permission: native.permission || '', retryable: Boolean(native.retryable) })}`);
    return { ok: false, retryable: Boolean(native.retryable) };
  }
  try {
    const { rings } = await clientPushDevicesApi.register(native.token, native.platform);
    console.info(`[clientPush] phone registered ${JSON.stringify({ platform: native.platform, rings })}`);
    return { ok: true };
  } catch (e) {
    console.warn(`[clientPush] registration failed ${JSON.stringify({ code: e?.code || '', status: e?.status || 0 })}`);
    return { ok: false, retryable: isRetryable(e) };
  }
}

/**
 * One later try for client `id`: only while that client is still the one signed in.
 * @returns {Promise<'stored' | 'retry' | 'stop'>}
 */
async function retryClientRegistration(id) {
  if (!isApiCustomer() || String(getCustomerSession()?.id || '') !== id) return 'stop';
  const res = await registerClientPhone();
  if (res.ok) return 'stored';
  return res.retryable ? 'retry' : 'stop';
}

/**
 * A tapped update: its delivery's screen. Parked first, since on a cold start
 * the OS opens the app into this tap before the router exists; the event
 * reaches a router already there.
 *
 * @param {{ link?: string, deliveryId?: string }} data
 */
export function openClientUpdate(data) {
  const id = String(data?.deliveryId || '').replace(/\D/g, '');
  const link = id ? `/live-tracking?delivery=${id}` : '/notifications';
  console.info(`[clientPush] update tapped ${JSON.stringify({ link })}`);
  try {
    sessionStorage.setItem(PARKED_KEY, link);
  } catch {
    // Private mode: the event below still reaches a mounted router.
  }
  try {
    window.dispatchEvent(new CustomEvent(TAP_EVENT, { detail: { link } }));
  } catch {
    // ignore
  }
}

/** The parked tap's link, once; null when there is none. */
export function consumeClientUpdateTap() {
  try {
    const link = sessionStorage.getItem(PARKED_KEY);
    sessionStorage.removeItem(PARKED_KEY);
    return link || null;
  } catch {
    return null;
  }
}

export { TAP_EVENT as CLIENT_UPDATE_TAP_EVENT };
